import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  ReviewQueueParams,
  MarkReviewedRequest,
  SetReviewStatusRequest,
  MatrixResolveRequest,
} from "../types/review";
import * as reviewApi from "../services/reviewApi";

export const reviewKeys = {
  all: ["reviews"] as const,
  queue: (params: ReviewQueueParams) =>
    [...reviewKeys.all, "queue", params] as const,
  queueGrouped: (params: ReviewQueueParams) =>
    [...reviewKeys.all, "queueGrouped", params] as const,
  queueCount: (source?: string) =>
    [...reviewKeys.all, "queueCount", source] as const,
  productChanges: (productId: number) =>
    [...reviewKeys.all, "productChanges", productId] as const,
  variantChanges: (variantId: number) =>
    [...reviewKeys.all, "variantChanges", variantId] as const,
  productOverview: (productId: number) =>
    [...reviewKeys.all, "productOverview", productId] as const,
  matrix: (sourceId: string, productIds?: number[]) =>
    [...reviewKeys.all, "matrix", sourceId, productIds] as const,
};

export function useReviewQueue(params: ReviewQueueParams = {}) {
  return useQuery({
    queryKey: reviewKeys.queue(params),
    queryFn: () => reviewApi.getReviewQueue(params),
  });
}

/** Queue with group-preserving pagination (groups never split across pages). Use for grouped review UI. */
export function useReviewQueueGrouped(params: ReviewQueueParams = {}) {
  return useQuery({
    queryKey: reviewKeys.queueGrouped(params),
    queryFn: () => reviewApi.getReviewQueueGrouped(params),
  });
}

export function useReviewQueueCount(source?: string) {
  return useQuery({
    queryKey: reviewKeys.queueCount(source),
    queryFn: () => reviewApi.getReviewQueueCount(source),
  });
}

export function useProductChanges(productId: number, enabled: boolean) {
  return useQuery({
    queryKey: reviewKeys.productChanges(productId),
    queryFn: () => reviewApi.getProductChanges(productId),
    enabled: enabled && productId > 0,
  });
}

export function useVariantChanges(variantId: number, enabled: boolean) {
  return useQuery({
    queryKey: reviewKeys.variantChanges(variantId),
    queryFn: () => reviewApi.getVariantChanges(variantId),
    enabled: enabled && variantId > 0,
  });
}

export function useProductOverview(productId: number, enabled: boolean) {
  return useQuery({
    queryKey: reviewKeys.productOverview(productId),
    queryFn: () => reviewApi.getProductOverview(productId),
    enabled: enabled && productId > 0,
  });
}

export function useMarkProductReviewed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      body,
    }: {
      productId: number;
      body: MarkReviewedRequest;
    }) => reviewApi.markProductReviewed(productId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

export function useMarkVariantReviewed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      variantId,
      body,
    }: {
      variantId: number;
      body: MarkReviewedRequest;
    }) => reviewApi.markVariantReviewed(variantId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

export function useMarkProductInProgress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      body,
    }: {
      productId: number;
      body: MarkReviewedRequest;
    }) => reviewApi.markProductInProgress(productId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

export function useMarkVariantInProgress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      variantId,
      body,
    }: {
      variantId: number;
      body: MarkReviewedRequest;
    }) => reviewApi.markVariantInProgress(variantId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

export function useSetProductReviewStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      body,
    }: {
      productId: number;
      body: SetReviewStatusRequest;
    }) => reviewApi.setProductReviewStatus(productId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

export function useSetVariantReviewStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      variantId,
      body,
    }: {
      variantId: number;
      body: SetReviewStatusRequest;
    }) => reviewApi.setVariantReviewStatus(variantId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

export function useSourceReviewMatrix(sourceId: string, productIds?: number[]) {
  return useQuery({
    queryKey: reviewKeys.matrix(sourceId, productIds),
    queryFn: () => reviewApi.getSourceReviewMatrix(sourceId, productIds),
    enabled: !!sourceId,
  });
}

export function useResolveSourceReviewMatrix() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      body,
    }: {
      sourceId: string;
      body: MatrixResolveRequest;
    }) => reviewApi.resolveSourceReviewMatrix(sourceId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

export function useReviewVariantGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      variantId,
      body,
    }: {
      variantId: number;
      body: SetReviewStatusRequest;
    }) => reviewApi.setVariantReviewGroupStatus(variantId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}
